import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, type FormEvent } from 'react'
import { useAuth } from '@/hooks/use-auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { SearchIcon as Search } from '@/components/icons/lucide-search'
import { MenuIcon as Menu } from '@/components/icons/lucide-menu'
import { MoonIcon as Moon } from '@/components/icons/lucide-moon'
import { SunIcon as Sun } from '@/components/icons/lucide-sun'
import { LogOutIcon as LogOut } from '@/components/icons/lucide-log-out'
import { SettingsIcon as Settings } from '@/components/icons/lucide-settings'
import { LayoutDashboardIcon as LayoutDashboard } from '@/components/icons/lucide-layout-dashboard'
import { MessageSquareIcon as MessageSquare } from '@/components/icons/lucide-message-square'
import { UserIcon as User } from '@/components/icons/lucide-user'
import { NotificationBadge } from '@/components/notifications'
import { useUIStore } from '@/stores/ui-store'
import { AfdianIcon } from '@/components/icons/simple-icons-afdian'
import { useOnlinePresenceCount } from '@/hooks/use-online-presence-count'
import { UsersIcon as Users } from '@/components/icons/lucide-users'
import { Tooltip } from '@/components/ui/tooltip'
import { guideAuthorInitial } from '@/lib/guide-author-display'
import { AppFeedbackDialog } from '@/components/feedback/app-feedback-dialog'
import { Lightbulb } from 'lucide-react'

export function Header() {
  const { user, isAuthenticated, signOut } = useAuth()
  const { theme, setTheme, toggleSidebar } = useUIStore()
  const onlineCount = useOnlinePresenceCount(user?.user_id ?? null)
  const navigate = useNavigate()
  const location = useLocation()
  const [headerSearch, setHeaderSearch] = useState('')
  const [feedbackOpen, setFeedbackOpen] = useState(false)

  useEffect(() => {
    if (location.pathname === '/search') {
      const q = new URLSearchParams(location.search).get('q')
      setHeaderSearch(q ?? '')
    } else {
      setHeaderSearch('')
    }
  }, [location.pathname, location.search])

  const submitHeaderSearch = (e: FormEvent) => {
    e.preventDefault()
    const q = headerSearch.trim()
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search')
  }

  const handleLogout = async () => {
    await signOut()
  }

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center gap-4 px-4 max-md:justify-between max-md:gap-6">
        {/* Mobile: menu + logo stay together; max-md:justify-between separates this block from actions */}
        <div className="flex min-w-0 items-center gap-3 max-md:gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="shrink-0 md:hidden"
            onClick={toggleSidebar}
          >
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/" className="flex min-w-0 items-center gap-2">
            <AfdianIcon size={32} className="shrink-0 text-primary" aria-hidden />
            <span className="hidden font-bold text-xl sm:inline-block">Comfyflow</span>
          </Link>
        </div>

        {/* Search bar — desktop/tablet */}
        <form
          onSubmit={submitHeaderSearch}
          className="mx-auto hidden max-w-md min-w-0 flex-1 sm:block"
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              name="q"
              value={headerSearch}
              onChange={(e) => setHeaderSearch(e.target.value)}
              placeholder="Search workflows, guides, forum, creators…"
              autoComplete="off"
              className="bg-muted/50 pl-10"
              aria-label="Search site"
            />
          </div>
        </form>

        {/* Actions — extra spacing between icons on mobile only */}
        <div className="flex shrink-0 items-center gap-2 max-md:gap-4">
          <Tooltip
            side="bottom"
            content="People browsing the site right now (live)."
          >
            <div
              className="flex cursor-default items-center gap-1.5 rounded-md border border-border/60 bg-muted/50 px-2 py-1 text-xs text-muted-foreground max-sm:px-1.5"
              role="status"
              aria-live="polite"
              aria-label={`${onlineCount} users online`}
            >
              <span
                className="h-2 w-2 shrink-0 rounded-full bg-emerald-500"
                aria-hidden
              />
              <Users className="h-3.5 w-3.5 shrink-0 max-sm:hidden" />
              <span className="tabular-nums font-medium text-foreground">
                {onlineCount}
              </span>
              <span className="hidden sm:inline">online</span>
            </div>
          </Tooltip>

          <Button variant="ghost" size="icon" className="shrink-0 sm:hidden" asChild>
            <Link to="/search" aria-label="Open search">
              <Search className="h-5 w-5" />
            </Link>
          </Button>

          {isAuthenticated ? (
            <>
              <NotificationBadge />

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 shrink-0 rounded-full">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={user?.avatar_url || undefined} />
                      <AvatarFallback>
                        {guideAuthorInitial({
                          username: user?.username ?? '',
                          display_name: user?.display_name ?? null,
                        })}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="w-56" align="end" forceMount>
                  <DropdownMenuItem asChild>
                    <Link
                      to={user?.username ? `/profile/${user.username}` : '/onboarding/profile'}
                      className="flex items-center"
                    >
                      <User className="mr-2 h-4 w-4" />
                      View profile
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard" className="flex items-center">
                      <LayoutDashboard className="mr-2 h-4 w-4" />
                      Dashboard
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile/settings" className="flex items-center">
                      <Settings className="mr-2 h-4 w-4" />
                      Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/support" className="flex items-center">
                      <MessageSquare className="mr-2 h-4 w-4" />
                      Support
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={(e) => {
                      e.preventDefault()
                      setFeedbackOpen(true)
                    }}
                    className="cursor-pointer"
                  >
                    <Lightbulb className="mr-2 h-4 w-4" />
                    Feedback
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} className="cursor-pointer">
                    {theme === 'dark' ? (
                      <>
                        <Sun className="mr-2 h-4 w-4" />
                        Light Mode
                      </>
                    ) : (
                      <>
                        <Moon className="mr-2 h-4 w-4" />
                        Dark Mode
                      </>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <AppFeedbackDialog
                open={feedbackOpen}
                onOpenChange={setFeedbackOpen}
                contextPath={`${location.pathname}${location.search}`}
              />
            </>
          ) : (
            <Button asChild className="shrink-0">
              <Link to="/auth">Sign in with Google</Link>
            </Button>
          )}
        </div>
      </div>
    </header>
  )
}
